import React from "react";

type CardDot = { colorClass: string; label: string };
type CardBreakdown = {
  colorClass?: string;
  label: string | React.ReactNode;
  amount: string | React.ReactNode;
  amountClassName?: string;
  showDot?: boolean;
};

export type PortfolioCardVariant = "default" | "warning" | "danger";

export interface PortfolioCardData {
  id: string;
  title: string;
  value: string | React.ReactNode;
  dots?: CardDot[];
  breakdown?: CardBreakdown[];
  icon?: React.ReactNode;
  variant?: PortfolioCardVariant;
  paymentDate?: { label: string; date: string };
  footerLayout?: "column" | "row";
}

const variantTitleClasses: Record<PortfolioCardVariant, string> = {
  default: "cx-text-text-tertiary",
  warning: "cx-text-orange-600",
  danger: "cx-text-red-800",
};

const variantValueClasses: Record<PortfolioCardVariant, string> = {
  default: "cx-text-text-primary",
  warning: "cx-text-orange-600",
  danger: "cx-text-red-800",
};

const variantBreakdownValueClasses: Record<PortfolioCardVariant, string> = {
  default: "cx-text-text-primary",
  warning: "cx-text-orange-600",
  danger: "cx-text-red-800",
};

export const PortfolioCard: React.FC<PortfolioCardData> = (props) => {
  const {
    title,
    value,
    dots,
    breakdown,
    icon,
    variant = "default",
    paymentDate,
    footerLayout = "column",
  } = props;

  const hasFooter = Boolean(
    (breakdown && breakdown.length > 0) ||
      paymentDate ||
      (dots && dots.length > 0)
  );

  return (
    <div className="cx-bg-background-default cx-border cx-border-neutral-light cx-shadow-sm cx-rounded-modal cx-p-6 cx-flex cx-flex-col cx-gap-4 cx-w-full">
      <div className="cx-flex cx-items-start cx-gap-3">
        {icon ? <span className="cx-shrink-0">{icon}</span> : null}
        <div className="cx-flex cx-flex-col cx-gap-1 cx-min-w-0">
          <div
            className={`cx-text-sm cx-font-medium ${variantTitleClasses[variant]}`}
          >
            {title}
          </div>
          <div
            className={`cx-text-2xl cx-font-extrabold cx-leading-tight ${variantValueClasses[variant]}`}
          >
            {value}
          </div>
        </div>
      </div>

      {hasFooter ? (
        <>
          <div className="cx-bg-neutral-light cx-h-px cx-w-full" />
          {breakdown && breakdown.length > 0 ? (
            <div
              className={`cx-flex cx-text-xs cx-text-text-secondary ${
                footerLayout === "row"
                  ? "cx-flex-row cx-flex-wrap cx-items-start cx-gap-8"
                  : "cx-flex-col cx-gap-2"
              }`}
            >
              {breakdown.map((b, i) => (
                <div key={i} className="cx-flex cx-flex-col cx-gap-0.5">
                  <span className="cx-text-[11px] cx-text-text-secondary cx-font-medium">
                    {b.label}:
                  </span>
                  <span className="cx-flex cx-items-center cx-gap-1.5">
                    {b.showDot ? (
                      <span
                        className={`cx-inline-block cx-w-1.5 cx-h-1.5 cx-rounded-full ${
                          b.colorClass ?? "cx-bg-red-600"
                        }`}
                      />
                    ) : null}
                    <span
                      className={`cx-text-xs cx-font-semibold ${
                        b.amountClassName ??
                        variantBreakdownValueClasses[variant]
                      }`}
                    >
                      {b.amount}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}
          {paymentDate || (dots && dots.length > 0) ? (
            <div className="cx-flex cx-gap-6 cx-items-center cx-w-full">
              {paymentDate ? (
                <div className="cx-flex cx-flex-col cx-gap-0.5">
                  <span className="cx-text-[10px] cx-text-text-secondary cx-font-medium">
                    {paymentDate.label}
                  </span>
                  <span className="cx-text-xs cx-text-text-primary cx-font-medium">
                    {paymentDate.date}
                  </span>
                </div>
              ) : null}
              {dots && dots.length > 0 ? (
                <div className="cx-flex cx-flex-col cx-gap-1 cx-text-[10px] cx-text-text-secondary">
                  {dots.map((d, i) => (
                    <div key={i} className="cx-flex cx-items-center cx-gap-1.5">
                      <span
                        className={`cx-inline-block cx-w-1.5 cx-h-1.5 cx-rounded-full ${d.colorClass}`}
                      />
                      <span>{d.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default PortfolioCard;
